import { Box, Button, Select, TextField } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import { SelectChangeEvent } from "@mui/material/Select";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useApiUrl } from "@refinedev/core";
import { Edit } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import userRole from "@warriortrading/warriortrading.hermes.common.feathers/enums/userRole";
import { ReadableText } from "components/readableText";
import { useCallback, useEffect, useState } from "react";
import { dataProvider } from "rest-data-provider";
import { config } from "../../config";
import AddBadges from "./addBadges";
import AddProductRoom from "./addProductRoom";

export const ProductsEdit = () => {
  const {
    saveButtonProps,
    refineCore: { queryResult },
    register,
    formState: { errors },
  } = useForm();
  const stockUrl = config().server.stock;
  const data = queryResult?.data?.data;
  const [value, setValue] = useState<string[]>([]);
  const handleChange = (event: SelectChangeEvent<typeof value>) => {
    setValue(
      typeof event.target.value === "string"
        ? event.target.value.split(",")
        : event.target.value
    );
  };
  type privilegesArrType = Array<{ value: string; type: string }>;
  const [privilegesArr, setPrivilegesArr] = useState<privilegesArrType>([]);
  const [createdAt, setCreatedAt] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  useEffect(() => {
    if (data) {
      const privileges = data?.privileges;
      setValue(privileges.split(","));
    }
    if (data?.createdAt) {
      setCreatedAt(new Date(data?.createdAt).toLocaleString());
    }
    if (data?.updatedAt) {
      setUpdatedAt(new Date(data?.updatedAt).toLocaleString());
    }
  }, [data]);

  const transformWidgets = (widgets: any) => {
    return Object.entries(widgets).map(([k, v]) => ({
      value: k,
      type: (v as any)?.name,
    }));
  };
  const privilegesData = useCallback(() => {
    return dataProvider(stockUrl)
      .getList({ resource: "stock-config" })
      .then((res) => {
        if (res) {
          const data = res?.data as any;
          const privilegesList = [
            ...transformWidgets(data?.toplist?.widget),
            ...transformWidgets(data?.alert?.widget),
            ...transformWidgets(data?.others?.widget),
          ];
          setPrivilegesArr(privilegesList);
        }
        return res.data;
      });
  }, [stockUrl]);
  useEffect(() => {
    privilegesData();
  }, [privilegesData]);

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("crmId", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.crmId}
          helperText={(errors as any)?.crmId?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="CrmProduct"
          name="crmId"
        />
        <FormControl
          fullWidth
          style={{ marginTop: "10px", marginBottom: "10px" }}
        >
          <InputLabel id="privileges">Privileges</InputLabel>
          <Select
            {...register("privileges", {})}
            multiple
            renderValue={(value) => (
              <ReadableText text={value.filter((i) => !!i).join(", ")} />
            )}
            labelId="privileges"
            value={value}
            label="Privileges"
            onChange={handleChange}
          >
            {privilegesArr.map((item: any) => {
              return (
                <MenuItem key={item.value} value={item.value}>
                  {item.type}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <TextField
          {...register("description", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.description}
          helperText={(errors as any)?.description?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Description"
          name="description"
        />
        <TextField
          {...register("createdAt", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.createdAt}
          helperText={(errors as any)?.createdAt?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="CreatedAt"
          name="createdAt"
          value={createdAt}
          disabled
        />
        <TextField
          {...register("updatedAt", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.updatedAt}
          helperText={(errors as any)?.updatedAt?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="UpdatedAt"
          name="updatedAt"
          value={updatedAt}
          disabled
        />
      </Box>
    </Edit>
  );
};

export const ProductsEdit2 = () => {
  const {
    refineCore: { queryResult },
  } = useForm();
  const columns: GridColDef[] = [
    { field: "room.title", headerName: "RoomTitle", minWidth: 120, flex: 1 },
    {
      field: "attendee",
      headerName: "Attendee",
      minWidth: 120,
      flex: 1,
      renderCell: function render({ row }) {
        return userRole.fromValue(row.role)?.name;
      },
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 120,
      flex: 1,
      renderCell: function render({ row }) {
        return (
          <>
            <Button variant="contained" onClick={() => removeRoom(row.id)}>
              REMOVE
            </Button>
          </>
        );
      },
    },
  ];
  const [rows, setRow] = useState([]);
  const [id, setId] = useState("");
  const apiUrl = useApiUrl();
  const fetchData = useCallback(
    (id: any) => {
      return dataProvider(apiUrl)
        .getList({
          resource: "product-room-relations",
          filters: [
            {
              field: "productId",
              operator: "eq",
              value: id,
            },
          ],
        })
        .then((res) => {
          if (res?.data) {
            const data = res?.data as any;
            setRow(data);
          }
          return res?.data;
        });
    },
    [apiUrl]
  );

  useEffect(() => {
    if (queryResult?.data?.data?.id) {
      setId(queryResult?.data?.data?.id + "");
      fetchData(queryResult?.data?.data?.id + "");
    }
  }, [queryResult?.data?.data.id, id, fetchData]);

  const removeRoom = async (rowId: any) => {
    await dataProvider(apiUrl).deleteOne({
      resource: "product-room-relation",
      id: rowId,
    });
    fetchData(id);
  };
  const [open, setOpen] = useState(false);
  const addProductRoom = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <Edit
      saveButtonProps={{ style: { display: "none" } }}
      headerProps={{
        style: {
          contentVisibility: "hidden",
        },
      }}
      breadcrumb={
        <div
          style={{
            display: "none",
          }}
        ></div>
      }
    >
      {open ? (
        <AddProductRoom
          isOpen={true}
          id={id}
          handleClose={handleClose}
          fetchRoomsData={fetchData}
        />
      ) : null}
      <h3 style={{ marginLeft: "5px" }}>Rooms Bound to This Product</h3>
      <div
        style={{ display: "flex", justifyContent: "end", marginBottom: "10px" }}
      >
        <Button variant="contained" onClick={addProductRoom}>
          +
        </Button>
      </div>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          autoHeight
        />
      </Box>
    </Edit>
  );
};

export const ProductsEdit3 = () => {
  const {
    refineCore: { queryResult },
  } = useForm();
  const columns: GridColDef[] = [
    {
      field: "badge.badgeUrl",
      headerName: "BadgeUrl",
      minWidth: 120,
      flex: 1,
      renderCell: function render({ value }) {
        return (
          <>
            <img alt="imgUrl" src={value} width={45} />
          </>
        );
      },
    },
    { field: "badge.code", headerName: "Code", minWidth: 120, flex: 1 },
    { field: "badge.name", headerName: "Name", minWidth: 120, flex: 1 },
    {
      field: "badge.expirationDate",
      headerName: "ExpirationDate",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 120,
      flex: 1,
      renderCell: function render({ row }) {
        return (
          <>
            <Button variant="contained" onClick={() => removeBadge(row.id)}>
              REMOVE
            </Button>
          </>
        );
      },
    },
  ];
  const [rows, setRow] = useState([]);
  const [id, setId] = useState("");
  const apiUrl = useApiUrl();
  const fetchData = useCallback(
    (id: any) => {
      return dataProvider(apiUrl)
        .getList({
          resource: "product-badge-relations",
          filters: [
            {
              field: "productId",
              operator: "eq",
              value: id,
            },
          ],
        })
        .then((res) => {
          if (res?.data) {
            const data = res?.data as any;
            setRow(data);
          }
          return res?.data;
        });
    },
    [apiUrl]
  );
  useEffect(() => {
    if (queryResult?.data?.data?.id) {
      setId(queryResult?.data?.data?.id + "");
      fetchData(queryResult?.data?.data?.id + "");
    }
  }, [queryResult?.data?.data.id, id, fetchData]);

  const removeBadge = async (rowId: any) => {
    await dataProvider(apiUrl).deleteOne({
      resource: "product-badge-relation",
      id: rowId,
    });
    fetchData(id);
  };
  const [open, setOpen] = useState(false);
  const addBadge = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <Edit
      saveButtonProps={{ style: { display: "none" } }}
      headerProps={{
        style: {
          contentVisibility: "hidden",
        },
      }}
      breadcrumb={
        <div
          style={{
            display: "none",
          }}
        ></div>
      }
    >
      {open ? (
        <AddBadges
          isOpen={true}
          id={id}
          handleClose={handleClose}
          rows={rows}
          fetchBadgeData={fetchData}
        />
      ) : null}
      <h3 style={{ marginLeft: "5px" }}>Badges Bound to This Product</h3>
      <div
        style={{ display: "flex", justifyContent: "end", marginBottom: "10px" }}
      >
        <Button variant="contained" onClick={addBadge}>
          +
        </Button>
      </div>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          autoHeight
        />
      </Box>
    </Edit>
  );
};
